import React, { useState, useRef, useEffect } from 'react';
import { Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { MagnifyingGlass, GlobeHemisphereWest, MapPin, MapTrifold, Robot, Eraser, X, Stack, ChartBar, Check, Info, PaperPlaneRight } from '@phosphor-icons/react';
import type { GeoChatMessage, GeoMapAction } from './types';
import SensitiveDataHint from '../chat/SensitiveDataHint';

const LOADING_STEP_ICONS = [MagnifyingGlass, GlobeHemisphereWest, MapPin, MapTrifold] as const;
const LOADING_STEPS = [
  { icon: 0, text: 'Analyzing map request...' },
  { icon: 1, text: 'Finding layers...' },
  { icon: 2, text: 'Calculating locations...' },
  { icon: 3, text: 'Building map view...' },
];

interface Props {
  messages: GeoChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  suggestions: string[];
  onSuggestionClick: (text: string) => void;
  onClearChat?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

function formatActionBadge(action: GeoMapAction): string | null {
  switch (action.action) {
    case 'toggle_layers': {
      const parts: string[] = [];
      if (action.layersOn?.length) parts.push(`Enabled: ${action.layersOn.join(', ')}`);
      if (action.layersOff?.length) parts.push(`Disabled: ${action.layersOff.join(', ')}`);
      return parts.join(' · ') || null;
    }
    case 'set_filter':
      return action.filter ? `Filter: ${action.filter.layerCode} → ${action.filter.filterValue}` : null;
    case 'clear_filter':
      return action.filter ? `Cleared filter: ${action.filter.layerCode}` : null;
    case 'enable_choropleth':
      return action.choropleth
        ? `Choropleth: ${action.choropleth.metric} (${action.choropleth.year})`
        : null;
    case 'disable_choropleth':
      return 'Choropleth disabled';
    case 'change_basemap':
      return action.basemap ? `Basemap: ${action.basemap}` : null;
    case 'zoom_to':
      return action.featureName ? `Zoomed to ${action.featureName}` : 'Zoomed to location';
    default:
      return null;
  }
}

const s = mergeStyleSets({
  root: {
    width: '35%',
    minWidth: 320,
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column' as const,
    borderLeft: '1px solid #E1E1E1',
    background: '#FFFFFF',
    flexShrink: 0,
  },
  collapsed: {
    position: 'fixed' as const,
    bottom: 24,
    right: 24,
    zIndex: 1100,
  },
  collapseBtn: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(46,135,61,0.35)',
    transition: 'transform 0.15s ease',
    selectors: { ':hover': { transform: 'scale(1.08)' } },
  },
  header: {
    padding: '12px 16px',
    borderBottom: '1px solid #E1E1E1',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  headerTitle: { fontSize: 13, fontWeight: 700, color: '#232323' },
  headerBeta: {
    fontSize: 10, fontWeight: 600, color: '#2e873d',
    background: '#d9f4d7', padding: '2px 8px', borderRadius: 999,
  },
  headerSpacer: { flex: 1 },
  clearBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 4, border: 'none',
    background: 'transparent', color: '#747474', fontSize: 11,
    cursor: 'pointer', transition: 'all 0.12s ease',
    selectors: { ':hover': { background: '#F5F5F5', color: '#D13438' } },
  },
  collapseHeaderBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '4px 6px', borderRadius: 4, border: 'none',
    background: 'transparent', color: '#747474', fontSize: 12,
    cursor: 'pointer',
    selectors: { ':hover': { background: '#F5F5F5' } },
  },
  messages: {
    flex: 1, overflow: 'auto', padding: '12px 16px',
    display: 'flex', flexDirection: 'column' as const, gap: 12,
  },
  msgUser: {
    alignSelf: 'flex-end', background: '#2e873d', color: '#FFFFFF',
    borderRadius: '12px 12px 2px 12px', padding: '8px 14px',
    fontSize: 13, maxWidth: '85%', lineHeight: '1.5',
    wordBreak: 'break-word' as const,
  },
  msgAssistant: {
    alignSelf: 'flex-start', background: '#F5F5F5', color: '#232323',
    borderRadius: '12px 12px 12px 2px', padding: '10px 14px',
    fontSize: 13, maxWidth: '85%', lineHeight: '1.5',
    wordBreak: 'break-word' as const,
  },
  actionBadge: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 6, padding: '6px 10px', borderRadius: 6,
    background: '#d9f4d7', fontSize: 11, fontWeight: 500, color: '#2e873d',
  },
  chipRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 6, marginTop: 8 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 500,
    background: '#FFFFFF', color: '#2e873d',
    border: '1px solid rgba(46,135,61,0.3)', cursor: 'pointer',
    transition: 'all 0.12s ease',
    selectors: {
      ':hover': { background: '#2e873d', color: '#FFFFFF', borderColor: '#2e873d' },
    },
  },
  emptyRoot: {
    display: 'flex', flexDirection: 'column' as const, gap: 20, padding: '24px 16px',
  },
  welcomeRow: { display: 'flex', alignItems: 'center', gap: 10 },
  welcomeAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2e873d, #55a655)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  capabilityCard: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '10px 12px', borderRadius: 8,
    background: '#F9F9F9', border: '1px solid #F0F0F0',
  },
  capabilityIcon: {
    width: 32, height: 32, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 600, color: '#747474',
    textTransform: 'uppercase' as const, letterSpacing: '0.04em',
  },
  tipText: {
    fontSize: 11, color: '#747474', lineHeight: '1.5',
    background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6,
    padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 6,
  },
  thinkingDots: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 14px', background: '#F5F5F5',
    borderRadius: '12px 12px 12px 2px', alignSelf: 'flex-start',
    fontSize: 13, color: '#747474',
  },
  inputArea: { borderTop: '1px solid #E1E1E1', padding: '10px 12px', flexShrink: 0 },
  inputRow: { display: 'flex', alignItems: 'center', gap: 8 },
  input: {
    flex: 1, border: '1px solid #E1E1E1', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 0.12s ease',
    selectors: { ':focus': { borderColor: '#2e873d' } },
  },
  sendBtn: {
    width: 36, height: 36, borderRadius: 8, border: 'none',
    background: '#2e873d', color: '#FFFFFF', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    transition: 'background 0.12s ease',
    selectors: {
      ':hover': { background: '#256d31' },
      ':disabled': { opacity: 0.5, cursor: 'default' },
    },
  },
});

export default function GeoChat({
  messages, onSend, loading, suggestions,
  onSuggestionClick, onClearChat, isCollapsed, onToggleCollapse,
}: Props) {
  const [text, setText] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading) { setLoadingStep(0); return; }
    const timer = setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, loadingStep]);

  const handleSend = () => {
    const t = text.trim();
    if (!t || loading) return;
    onSend(t);
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (isCollapsed) {
    return (
      <div className={s.collapsed}>
        <button className={s.collapseBtn} onClick={onToggleCollapse} title="Open Map Assistant">
          <Robot size={22} weight="fill" />
        </button>
      </div>
    );
  }

  return (
    <div className={s.root}>
      {/* Header */}
      <div className={s.header}>
        <Robot size={16} weight="fill" style={{ color: '#2e873d' }} />
        <span className={s.headerTitle}>Map Assistant AI</span>
        <span className={s.headerBeta}>Beta</span>
        <div className={s.headerSpacer} />
        {onClearChat && messages.length > 0 && (
          <button className={s.clearBtn} onClick={onClearChat} title="Clear chat history">
            <Eraser size={12} weight="bold" />
            Clear
          </button>
        )}
        {onToggleCollapse && (
          <button className={s.collapseHeaderBtn} onClick={onToggleCollapse} title="Collapse chat">
            <X size={10} weight="bold" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className={s.messages}>
        {messages.length === 0 && !loading && (
          <div className={s.emptyRoot}>
            <div className={s.welcomeRow}>
              <div className={s.welcomeAvatar}>
                <Robot size={18} weight="fill" style={{ color: '#FFFFFF' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#232323' }}>Hi! I'm your map assistant</div>
                <div style={{ fontSize: 12, color: '#747474', marginTop: 2 }}>Ask me to show layers, filter data, or explore census maps.</div>
              </div>
            </div>

            <div>
              <div className={s.sectionLabel} style={{ marginBottom: 8 }}>What I can do</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#d9f4d7' }}>
                    <Stack size={15} weight="fill" style={{ color: '#2e873d' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Layer Control</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Show or hide parks, schools, heritage sites and more</div>
                  </div>
                </div>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#E8F0FE' }}>
                    <ChartBar size={15} weight="fill" style={{ color: '#2563EB' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Census Maps</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Colour the map by population, income, or housing data</div>
                  </div>
                </div>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#FEF3C7' }}>
                    <MagnifyingGlass size={15} weight="bold" style={{ color: '#B45309' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Spatial Search</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Find features near suburbs or filter by attributes</div>
                  </div>
                </div>
                <div className={s.capabilityCard}>
                  <div className={s.capabilityIcon} style={{ background: '#FCE7F3' }}>
                    <MapTrifold size={15} weight="fill" style={{ color: '#BE185D' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#232323' }}>Map Navigation</div>
                    <div style={{ fontSize: 11, color: '#747474', marginTop: 1 }}>Zoom to suburbs, switch basemaps, and explore</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={s.tipText}>
              <Info size={12} weight="fill" style={{ color: '#B45309', marginTop: 1, flexShrink: 0 }} />
              <span>Try "Show me all parks" or "Colour the map by median income"</span>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <React.Fragment key={msg.id}>
            <div className={msg.role === 'user' ? s.msgUser : s.msgAssistant}>
              {msg.content}
              {msg.role === 'assistant' && msg.mapAction && msg.mapAction.action !== 'insight' && (() => {
                const badge = formatActionBadge(msg.mapAction);
                return badge ? (
                  <div className={s.actionBadge}>
                    <Check size={11} weight="bold" />
                    {badge}
                  </div>
                ) : null;
              })()}
            </div>
            {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && (
              <div className={s.chipRow}>
                {msg.suggestions.map((sug, i) => (
                  <button key={i} className={s.chip} onClick={() => onSuggestionClick(sug)}>
                    {sug}
                  </button>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}

        {loading && (
          <div className={s.thinkingDots}>
            <Spinner size={SpinnerSize.xSmall} />
            {React.createElement(LOADING_STEP_ICONS[LOADING_STEPS[loadingStep].icon], { size: 13, weight: 'bold', style: { color: '#2e873d' } })}
            {LOADING_STEPS[loadingStep].text}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Suggestions for empty state */}
      {messages.length === 0 && suggestions.length > 0 && !loading && (
        <div style={{ padding: '0 16px 8px' }}>
          <div className={s.sectionLabel} style={{ marginBottom: 6 }}>Start with one of these</div>
          <div className={s.chipRow}>
            {suggestions.map((sug, i) => (
              <button key={i} className={s.chip} onClick={() => onSuggestionClick(sug)}>
                {sug}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className={s.inputArea}>
        <div className={s.inputRow}>
          <input
            className={s.input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about layers, census data, or locations..."
            disabled={loading}
          />
          <button className={s.sendBtn} onClick={handleSend} disabled={loading || !text.trim()}>
            <PaperPlaneRight size={14} weight="fill" />
          </button>
        </div>
        <SensitiveDataHint style={{ marginTop: 8 }} />
      </div>
    </div>
  );
}
